// GPU unpack of mat_mod's output (one-shot at load), replacing the ~280 MiB
// GPU→CPU readback + CPU crop/permute (matMod.js:unpackMatMod). Keeps matUv on
// the GPU (ORT gpu-buffer output) and fans it into the three downstream buffers
// in one compute dispatch: the GPU-shade static-attr buffer (albedo lanes), the
// resident bsdf feature buffer (f_dec), and the resident spec_mod input buffer
// (f_dec_pad channel-planes). See wgsl/unpack_matmod.wgsl.
//
// Same compute-pass shape as SpecViewTiler (pipeline + uniform allocated once).
// Runs once, so the bind group is rebuilt per call rather than cached.

import unpackShader from './wgsl/unpack_matmod.wgsl?raw';

const MAX_WG = 65535; // maxComputeWorkgroupsPerDimension floor across GPUs

export class MatModUnpacker {
  constructor() {
    this.device = null;
    this.pipeline = null;
    this.uniform = null;
  }

  _ensure(device) {
    if (this.device === device && this.pipeline) return;
    this.device = device;
    this.pipeline = device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: device.createShaderModule({ code: unpackShader }),
        entryPoint: 'main',
      },
    });
    // 2×vec4<u32>: a=(P,N,nPadSrc,planeSrc) b=(nPadDst,planeDst,0,0).
    this.uniform = device.createBuffer({ size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
  }

  /**
   * @param device    GPUDevice (ORT's shared webgpu device)
   * @param matUv     GPUBuffer — mat_mod's gpu-buffer output (1,35,sPad,nPad) f32
   * @param targets   { bStatic, bBsdfFeat, bSpecIn } GPUBuffers written in place
   * @param dims      { P, N, nPadSrc, planeSrc, nPadDst, planeDst } — matUv is read
   *                  at the src (mat_mod padded) grid; bSpecIn is written at the
   *                  dst grid, which is tighter when the pack's spec_mod takes
   *                  dynamic dims. dst == src on a padded-grid pack.
   */
  unpack(device, matUv, targets, { P, N, nPadSrc, planeSrc, nPadDst, planeDst }) {
    this._ensure(device);
    device.queue.writeBuffer(this.uniform, 0,
      new Uint32Array([P, N, nPadSrc, planeSrc, nPadDst, planeDst, 0, 0]));
    const bind = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: { buffer: matUv } },
        { binding: 2, resource: { buffer: targets.bStatic } },
        { binding: 3, resource: { buffer: targets.bBsdfFeat } },
        { binding: 4, resource: { buffer: targets.bSpecIn } },
      ],
    });
    const enc = device.createCommandEncoder({ label: 'hair-matmod-unpack' });
    const pass = enc.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, bind);
    pass.dispatchWorkgroups(Math.min(Math.ceil(P / 64), MAX_WG));
    pass.end();
    device.queue.submit([enc.finish()]);
  }

  dispose() {
    try { this.uniform?.destroy?.(); } catch { /* ignore */ }
    this.device = this.pipeline = this.uniform = null;
  }
}
